import React from "react";
import { Sparkles, ArrowRight } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { formatDate } from "@/lib/format";
import { Link } from "react-router-dom";

/**
 * "Welcome back" banner shown above the upload zone for authed users
 * who already have prior uploads. Tells them where they left off.
 */
export default function WelcomeBackBanner() {
  const { user, meta } = useAuth();
  if (!user) return null;
  const total = meta?.totalInvoices || 0;
  const last = meta?.lastUploadAt;
  const reportId = meta?.latestReportId;
  if (total === 0 || !last) return null;

  const since = friendlyDate(last);

  return (
    <div className="cartiq-card relative overflow-hidden mb-6" data-testid="welcome-back-banner">
      <div className="accent-bar absolute top-0 left-0 right-0" />
      <div className="grid md:grid-cols-[1fr_auto] gap-4 items-center">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#FF6B00] font-bold mb-1.5 flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" />
            Welcome back, {(user.name || user.email).split(" ")[0]}
          </div>
          <h3 className="font-display uppercase text-2xl md:text-3xl">
            You've analysed{" "}
            <span className="text-[#FF6B00] font-mono-num">{total}</span>{" "}
            {total === 1 ? "invoice" : "invoices"} so far.
          </h3>
          <p className="text-sm text-[#888] mt-1.5 leading-relaxed">
            Last upload: <span className="text-[#F0EDE8] font-bold">{since}</span>.
            Drop new invoices below — duplicates are auto-skipped, and your report keeps growing.
          </p>
        </div>
        {reportId && (
          <Link
            to={`/dashboard/${reportId}`}
            className="btn-saffron flex items-center justify-center gap-2 text-sm whitespace-nowrap"
            data-testid="welcome-back-resume-btn"
          >
            Open my dashboard <ArrowRight className="w-4 h-4" />
          </Link>
        )}
      </div>
    </div>
  );
}

function friendlyDate(iso) {
  // Always include the actual calendar date so the user knows exactly how
  // recent their data is. The friendly relative label ("today", "yesterday",
  // "3 days ago") is appended in parens for context — no more guessing what
  // "yesterday" means a week later.
  try {
    const d = new Date(iso);
    const exact = formatDate(iso); // e.g. "06 May 2026"
    const now = new Date();
    const diff = Math.round((now - d) / (1000 * 60 * 60 * 24));
    if (diff <= 0) return `${exact} (today)`;
    if (diff === 1) return `${exact} (yesterday)`;
    if (diff < 7) return `${exact} (${diff} days ago)`;
    return exact;
  } catch {
    return formatDate(iso);
  }
}
